/**
 * Data-source register hooks (P252) -- CRUD over the onboarding data
 * inventory.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { api } from "shared/api/client";
import type { DataSource, DataSourceInput } from "shared/api/types";

const KEY = ["admin-data-sources"] as const;

export function useDataSources(): UseQueryResult<DataSource[]> {
  return useQuery<DataSource[]>({
    queryKey: KEY,
    queryFn: () => api.get<DataSource[]>("/v1/admin/data-sources"),
  });
}

export function useCreateDataSource(): UseMutationResult<
  DataSource,
  Error,
  DataSourceInput
> {
  const qc = useQueryClient();
  return useMutation<DataSource, Error, DataSourceInput>({
    mutationFn: (body) => api.post<DataSource>("/v1/admin/data-sources", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateDataSource(): UseMutationResult<
  DataSource,
  Error,
  { id: string; patch: Partial<DataSourceInput> }
> {
  const qc = useQueryClient();
  return useMutation<
    DataSource,
    Error,
    { id: string; patch: Partial<DataSourceInput> }
  >({
    mutationFn: ({ id, patch }) =>
      api.patch<DataSource>(`/v1/admin/data-sources/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteDataSource(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete(`/v1/admin/data-sources/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
